import { Send, Upload } from "lucide-react";
import type { FC } from "react";
import { Link } from "react-router-dom";

import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

import type { SendIntakeTarget } from "./sendIntake";

type Props = {
  onUpload: () => void;
  intake: SendIntakeTarget;
  /** Files are being dragged over the pane. */
  dragging?: boolean;
  className?: string;
};

/**
 * The Case Files pane before anything is uploaded: upload what the firm has,
 * or send the client the visa's intake to collect it. Either can come first,
 * and nothing runs on its own after an upload.
 */
export const CaseFilesStart: FC<Props> = ({
  onUpload,
  intake,
  dragging = false,
  className
}) => (
  <div
    className={cn("flex flex-col gap-2 px-3 py-2", className)}
    data-testid="case-files-start"
  >
    <p
      className={cn("text-sm text-fg-4", dragging && "text-accent-seed")}
      data-testid="case-files-start-hint"
    >
      {dragging ? "Drop to upload" : "No files yet. Drop them here, or:"}
    </p>
    <div className="grid grid-cols-2 gap-1.5">
      <Button size="sm" onClick={onUpload}>
        <Upload />
        Upload Files
      </Button>
      <Tooltip>
        <TooltipTrigger asChild>
          {intake.to ? (
            <Button size="sm" variant="secondary" asChild>
              <Link to={intake.to} data-testid="case-files-start-intake">
                <Send />
                {intake.label}
              </Link>
            </Button>
          ) : (
            <span className="grid">
              <Button
                size="sm"
                variant="secondary"
                disabled
                data-testid="case-files-start-intake"
              >
                <Send />
                {intake.label}
              </Button>
            </span>
          )}
        </TooltipTrigger>
        <TooltipContent side="bottom">{intake.hint}</TooltipContent>
      </Tooltip>
    </div>
  </div>
);
